import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Wine, 
  Leaf, 
  MapPin, 
  Star, 
  Crown, 
  Sparkle,
  Trophy,
  Heart,
  Lightning,
  CurrencyDollar,
  TrendUp
} from '@phosphor-icons/react';
import { InstantSearch, Index, Configure, RefinementList } from 'react-instantsearch';
import { searchClient } from '../../services/algoliaClient';
import { categories, getFilterAttribute } from '../../data/categories';

// Category Card Component
const CategoryCard = ({ category, icon: Icon, count, onClick }) => {
  return (
    <button 
      className="category-card"
      onClick={onClick}
      aria-label={`Voir ${category.name}`}
    >
      <div className="category-icon">
        <Icon size={24} />
      </div>
      <div className="category-info">
        <h3 className="category-name">{category.name}</h3>
        <span className="category-count">{count} produits</span>
      </div>
    </button>
  );
};

// Category Section Component
const CategorySection = ({ title, icon: Icon, categories, filterType }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleCategoryClick = (category) => {
    if (filterType) {
      // For grape varieties and regions, use the structured URL format
      const type = filterType === 'region' ? 'regions' : 
                   filterType === 'grape' ? 'cepages' : 
                   filterType === 'country' ? 'pays' : 'cepages';
      navigate(`/categories/${type}/${category.slug}`);
    } else {
      // For wine types, use the regular category page
      navigate(`/categories/${category.slug}`);
    }
  };

  return (
    <section className="category-section">
      <div className="category-section-header">
        <Icon size={28} />
        <h2 className="category-section-title">{title}</h2>
      </div>
      
      <div className="category-carousel">
        {categories.map((category, index) => (
          <CategoryCard
            key={index}
            category={category}
            icon={category.icon}
            count={category.count || 0}
            onClick={() => handleCategoryClick(category)}
          />
        ))}
      </div>
    </section>
  );
};

// Categories Page Component
export const CategoriesPage = () => {
  const { t } = useTranslation();

  // Use the actual categories data with sample counts
  const categoryData = {
    wineTypes: categories.map(cat => ({
      ...cat,
      count: Math.floor(Math.random() * 500) + 100 // Sample counts for demo
    })),
    regions: [
      { name: 'Bordeaux', count: 450, icon: MapPin, slug: 'bordeaux' },
      { name: 'Bourgogne', count: 380, icon: MapPin, slug: 'bourgogne' },
      { name: 'Champagne', count: 180, icon: MapPin, slug: 'champagne' },
      { name: 'Loire', count: 220, icon: MapPin, slug: 'loire' },
      { name: 'Rhône', count: 195, icon: MapPin, slug: 'rhone' },
      { name: 'Alsace', count: 120, icon: MapPin, slug: 'alsace' }
    ],
    grapeVarieties: [
      { name: 'Cabernet Sauvignon', count: 280, icon: Leaf, slug: 'cabernet-sauvignon' },
      { name: 'Merlot', count: 320, icon: Leaf, slug: 'merlot' },
      { name: 'Pinot Noir', count: 195, icon: Leaf, slug: 'pinot-noir' },
      { name: 'Chardonnay', count: 245, icon: Leaf, slug: 'chardonnay' },
      { name: 'Sauvignon Blanc', count: 180, icon: Leaf, slug: 'sauvignon-blanc' },
      { name: 'Syrah', count: 165, icon: Leaf, slug: 'syrah' }
    ]
  };

  return (
    <main className="categories-page">
      <div className="categories-hero">
        <h1 className="categories-title">Catégories de Vins</h1>
        <p className="categories-subtitle">
          Explorez notre collection par type, région, cépage et plus encore
        </p>
      </div>

      <div className="categories-content">
        {/* Wine Types - Using actual product identities */}
        <CategorySection
          title="Types de Vins"
          icon={Wine}
          categories={categoryData.wineTypes}
        />

        {/* Regions */}
        <CategorySection
          title="Régions Viticoles"
          icon={MapPin}
          categories={categoryData.regions}
          filterType="region"
        />

        {/* Grape Varieties */}
        <CategorySection
          title="Cépages"
          icon={Leaf}
          categories={categoryData.grapeVarieties}
          filterType="grape"
        />
      </div>
    </main>
  );
}; 